/**
 * Pinned columns and "Compare lines" (W2-04a; DESIGN.md §4.7 "Pinned columns / Compare lines (Full)", §5.5):
 *
 *  - Pin column (column header menu, `columns.pin`): keeps that (line, property, layer) column next to the active
 *    line's columns, also after the active line changes; pinning again unpins.
 *  - Compare lines (palette, Lines panel context menu, `columns.compare`): pins ONE property of every selected line
 *    side by side (the active line's own column is already shown). The host's marquee spans pinned columns, and K,
 *    the easing picker and templates act on every selected line / selected segment, so several lines are edited
 *    together.
 * Pinned columns are editor-store state (`pinnedColumns`), persisted per project with the view state; the column
 * factory relayouts the host whenever they change.
 */

import type { LineId, LineProp } from '../../core/model/types';
import type { ColumnRef } from '../../state/types';
import { isLayered, sameRef } from './columnSpec';
import { propNameAcross, propNames } from './format';
import { propCtxOf } from './motionActions';
import { m } from './motion.messages';
import { ref, type Services } from './txn';

/** True when the column is pinned. */
export function isPinned(s: Pick<Services, 'editor'>, col: ColumnRef): boolean {
  return s.editor.getState().pinnedColumns.some((c) => sameRef(c, col));
}

function lineName(s: Services, line: LineId): string {
  return s.store?.doc.lines.get(line)?.name ?? String(line);
}

function propLabel(s: Services, col: ColumnRef): string {
  const store = s.store;
  const ctx = store === null ? { kind: 'normal' as const, uiMover: false } : propCtxOf(store, col.line);
  return s.i18n.t(propNames(col.prop, ctx).full);
}

/** Pins or unpins a column; returns the new state. Pinning makes the motion columns visible. */
export function togglePin(s: Services, col: ColumnRef, quiet = false): 'pinned' | 'unpinned' {
  const st = s.editor.getState();
  const was = st.pinnedColumns.some((c) => sameRef(c, col));
  if (was) {
    st.setPinnedColumns(st.pinnedColumns.filter((c) => !sameRef(c, col)));
    if (!quiet)
      s.ui.toast(ref(m.unpinned, { prop: propLabel(s, col), line: lineName(s, col.line) }), {
        kind: 'info',
        durationMs: 2500,
      });
    return 'unpinned';
  }
  st.setPinnedColumns([...st.pinnedColumns, col]);
  if (!st.timeline.showMotionColumns) st.setTimeline({ showMotionColumns: true });
  if (!quiet)
    s.ui.toast(ref(m.pinned, { prop: propLabel(s, col), line: lineName(s, col.line) }), {
      kind: 'info',
      durationMs: 2500,
    });
  return 'pinned';
}

export function clearPinned(s: Pick<Services, 'editor'>): void {
  const st = s.editor.getState();
  if (st.pinnedColumns.length > 0) st.setPinnedColumns([]);
}

/** Lines selected for Compare lines, in export order (the active line included when selected). */
export function compareCandidates(s: Pick<Services, 'store' | 'editor'>): LineId[] {
  const store = s.store;
  if (store === null) return [];
  const sel = s.editor.getState().selection.lines;
  return store.doc.lineOrder.filter((id) => sel.has(id));
}

/**
 * Compare lines: pins `prop` (on the active layer for summed properties, else layer 0) of every selected line other
 * than the active one, replacing the previous pinned set, and focuses the property's column. Returns the pins.
 * A line that has no keyframes on the active layer of a summed property shows its base layer (W3-05: an empty
 * layer column compared nothing); a line without any track of the property keeps the active layer (K adds there).
 */
export function compareLines(s: Services, prop: LineProp, lines: readonly LineId[]): ColumnRef[] {
  const st = s.editor.getState();
  const layer = isLayered(prop) ? Math.max(0, st.activeLayer) : 0;
  let active = st.activeLine;
  if (active === null || !lines.includes(active)) {
    active = lines[0] ?? null;
    if (active !== null) st.setActiveLine(active);
  }
  const store0 = s.store;
  const layerFor = (line: LineId): number => {
    if (layer === 0 || store0 === null) return layer;
    if (store0.idx.track(line, prop, layer) !== undefined) return layer;
    return store0.idx.track(line, prop, 0) !== undefined ? 0 : layer;
  };
  const pins = lines.filter((l) => l !== active).map((line): ColumnRef => ({ line, prop, layer: layerFor(line) }));
  const cur = s.editor.getState();
  cur.setPinnedColumns(pins);
  if (!cur.timeline.showMotionColumns) cur.setTimeline({ showMotionColumns: true });
  if (active !== null) cur.setFocusedColumn({ line: active, prop, layer });
  const store = s.store;
  const ctxs = store === null ? [] : lines.filter((l) => store.doc.lines.has(l)).map((l) => propCtxOf(store, l));
  s.ui.toast(
    ref(m.compareStarted, {
      prop: s.i18n.t(propNameAcross(prop, ctxs)),
      count: lines.length,
    }),
    {
      kind: 'info',
      action: { label: ref(m.compareClear), command: 'motion.clearPinned' },
    },
  );
  return pins;
}
